<template>
  <form-block
    :title="tr('Ad set name')"
    :description="tr('This will be the name of the currently created adset. If you are not sure what should be the name of your adset, you can also add different macros that will automatically be filled according to your current ad series settings.')"
  >
    <div class="flex flex-col gap-1.5">
      <ui-label for="adset-name-prefix">{{ tr('Name prefix') }}</ui-label>
      <ui-input
        id="adset-name-prefix"
        v-model="entity.settings.name_prefix"
        placeholder="[Infinite.ad]"
        :disabled="disabled"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <ui-label>{{ tr('Ad set name') }}</ui-label>
      <templateable-input
        v-model="entity.settings.adset_name"
        :template-parts="adsetNameMacros"
        :placeholder="tr('Set the ad set name')"
        :disabled="disabled"
      />
    </div>

    <p class="m-0 text-sm text-muted-foreground [overflow-wrap:anywhere]">
      {{ tr('Preview') }}: <span class="text-foreground">{{ preview }}</span>
      <loader-circle-icon v-if="previewIsLoading" class="ml-1 inline size-4 animate-spin align-[-0.2em]" />
    </p>
  </form-block>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import ToolService from '@/services/Tool.js';
import TemplateableInput from '@/ui/components/TemplateableInput.vue';
import debounce from '@/utils/debounce.js';

export default {
  name: 'AdsetNameBlock',
  components: {
    LoaderCircleIcon: LoaderCircle,
    TemplateableInput,
  },
  mixins: [ RuleFormBlockMixin ],
  data() {
    return {
      preview: '–',
      previewIsLoading: false,
      previewGenerator: debounce(() => this.generatePreview(), 500),
    };
  },
  computed: {
    adsetNameMacros() {
      const result = [];
      let previousGroup = '';
      for (const item of this.$store.getters['app/adsetNameMacros']) {
        if (item.group !== previousGroup) {
          result.push({ title: this.tr(item.group) });
          previousGroup = item.group;
        }
        result.push({ label: this.tr(item.label), value: item.machine_name });
      }
      return result;
    },
  },
  watch: {
    'entity.settings.name_prefix'() {
      this.previewGenerator();
    },
    'entity.settings.adset_name'() {
      this.previewGenerator();
    },
  },
  created() {
    this.generatePreview();
  },
  methods: {
    validate() {
      if (!this.entity.settings.adset_name.trim()) {
        this.error(this.tr('The {field} field cannot be empty!', { '{field}': this.tr('Ad set') }));
        return false;
      }

      return true;
    },
    generatePreview() {
      if (this.previewIsLoading) {
        return;
      }

      this.previewIsLoading = true;

      const campaign = this.entity.output_campaign.toObject();
      campaign.ad_account = this.entity.ad_account.toObject();
      campaign.name = this.entity.campaignName;

      ToolService.getExampleAdsetName({
        prefix: this.entity.settings.name_prefix,
        template: this.entity.settings.adset_name,
        sourceProfileId: Array.isArray(this.entity.source_profiles) && this.entity.source_profiles.length !== 0 ? this.entity.source_profiles[0].id : null,
        adAccountId: this.entity.ad_account.id,
        campaign,
      })
        .then(resp => {
          if (resp) {
            this.preview = resp;
          }
          else {
            this.preview = '–';
          }

          return resp;
        })
        .catch(() => this.preview = '–')
        .finally(() => this.previewIsLoading = false);
    },
  },
};
</script>
